import { inject, Injectable } from '@angular/core';
import { FavoriteList } from '../types/favorite-list';
import { Store } from '@softline/core';
import {
  MessageBarStore,
  ModalStore,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  SOFTLINE_FEATURE_MODAL,
  TranslationService,
} from '@softline/ui-core';
import { AddFavoriteListDialogComponent } from '../dialogs/add-favorite-list-dialog/add-favorite-list-dialog.component';
import { SOFTLINE_FEATURE_ARTIKEL_INFO_FAVORITE_LISTS } from '../artikel-info.shared';
import { FavoriteListsStore } from '../store/favorite-lists.store';
import { showRequestErrors } from '@softline/application';
import { Item } from '../types/item';
import { Favorite } from '../types/favorite';
import { AddFavoriteItemDialogComponent } from '../dialogs/add-favorite-item-dialog/add-favorite-item-dialog.component';

const FAVORITES_MESSAGE_GROUP = 'ARTIKEL_INFO_FAVORITES';

@Injectable()
export class FavoriteService {
  private readonly translations = inject(TranslationService);

  constructor(private store: Store) {}

  async removeList(list: FavoriteList): Promise<void> {
    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.ask,
      {
        title: this.translations.translate('#ARTIKELINFO.MESSAGES.REMOVE_LIST.TITLE')(),
        question: this.translations.translate(
          '#ARTIKELINFO.MESSAGES.REMOVE_LIST.QUESTION',
          { name: list.name }
        )(),
      }
    );

    if (!result || result === 'DISMISSED' || result !== 'YES') return;

    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_ARTIKEL_INFO_FAVORITE_LISTS,
        FavoriteListsStore.actions.delete,
        { entity: list }
      );

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        {
          title: this.translations.translate(
            '#ARTIKELINFO.MESSAGES.REMOVE_LIST_SUCCESS.TITLE'
          )(),
          message: this.translations.translate(
            '#ARTIKELINFO.MESSAGES.REMOVE_LIST_SUCCESS.MESSAGE',
            { name: list.name }
          )(),
        }
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  async addItemToList(
    item: Item
  ): Promise<{ favorite: Favorite; list: FavoriteList } | null | undefined> {
    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open<
        { favorite: Favorite; list: FavoriteList } | null | undefined,
        object
      >(),
      {
        component: AddFavoriteItemDialogComponent,
        dismiss: true,
        data: {
          item,
        },
      }
    );

    if (!result || result === 'DISMISSED') return undefined;

    await this.store.dispatch(
      SOFTLINE_FEATURE_MESSAGE_BAR,
      MessageBarStore.actions.success,
      {
        title: this.translations.translate(
          '#ARTIKELINFO.MESSAGES.ADD_ITEM_SUCCESS.TITLE'
        )(),
        message: this.translations.translate(
          '#ARTIKELINFO.MESSAGES.ADD_ITEM_SUCCESS.MESSAGE',
          { item: item.name ?? item?.title1, list: result.list.name }
        )(),
      }
    );

    return result;
  }

  async createList(): Promise<FavoriteList | null | undefined> {
    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open<FavoriteList | null | undefined, object>(),
      {
        component: AddFavoriteListDialogComponent,
        dismiss: true,
        priority: 999,
      }
    );

    if (!result || result === 'DISMISSED') return undefined;

    await this.store.dispatch(
      SOFTLINE_FEATURE_MESSAGE_BAR,
      MessageBarStore.actions.success,
      {
        title: this.translations.translate(
          '#ARTIKELINFO.MESSAGES.CREATE_LIST_SUCCESS.TITLE'
        )(),
        message: this.translations.translate(
          '#ARTIKELINFO.MESSAGES.CREATE_LIST_SUCCESS.MESSAGE',
          { name: result.name }
        )(),
      }
    );

    return result;
  }
}
